import { getGatewayBaseUrl } from "../config";
import { CashSaleVoidGatewayClient } from "../gateway/cash-sale-void-client";
import { CreditSaleVoidGatewayClient } from "../gateway/credit-sale-void-client";
import { posRuntimeDb, type OutboxRecord } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

export type LocalSaleVoidKind = "CASH" | "CREDIT";

export type LocalSaleVoidSnapshot = {
  schema: "LOCAL_SALE_VOID_V1";
  localVoidId: string;
  requestKey: string;
  organizationId: string;
  branchId: string;
  nodeId: string;
  performingEmployeeId: string;
  orderId: string;
  orderNo: string;
  kind: LocalSaleVoidKind;
  reason: string;
  sourceSurface: "SALES_HISTORY" | "LATEST_RECEIPT";
  localCommittedAt: string;
  canonicalResult: unknown | null;
};

type LocalSaleVoidOutboxPayload = {
  schema: "LOCAL_SALE_VOID_OUTBOX_V1";
  orderId: string;
  reason: string;
  kind: LocalSaleVoidKind;
  localVoidId: string;
  localCommittedAt: string;
  sourceSurface: LocalSaleVoidSnapshot["sourceSurface"];
};

export type CommitLocalSaleVoidInput = {
  identity: TrustedTerminalIdentity;
  performingEmployeeId: string;
  orderId: string;
  orderNo: string;
  kind: LocalSaleVoidKind;
  reason: string;
  requestKey: string;
  sourceSurface: LocalSaleVoidSnapshot["sourceSurface"];
};

function voidScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function localVoidId(requestKey: string) {
  return `sale-void:${requestKey}`;
}

function operation(kind: LocalSaleVoidKind) {
  return kind === "CASH" ? "VOID_COMMERCE_CASH_SALE" : "VOID_COMMERCE_CREDIT_SALE";
}

function isSaleVoidOperation(value: string) {
  return value === "VOID_COMMERCE_CASH_SALE" || value === "VOID_COMMERCE_CREDIT_SALE";
}

export async function commitLocalSaleVoid(input: CommitLocalSaleVoidInput) {
  const reason = input.reason.trim();
  if (!reason) throw new Error("SALE_VOID_REASON_REQUIRED");
  if (!input.requestKey.trim()) throw new Error("SALE_VOID_REQUEST_KEY_REQUIRED");

  const existing = await posRuntimeDb.outbox.get(input.requestKey);
  if (existing) {
    if (!isSaleVoidOperation(existing.operation)) throw new Error("REQUEST_KEY_CONFLICT");
    const journal = await posRuntimeDb.journal.where("requestKey").equals(input.requestKey).first();
    return journal?.snapshot as LocalSaleVoidSnapshot | undefined;
  }

  const now = new Date().toISOString();
  const scopeKey = voidScope(input.identity);
  const id = localVoidId(input.requestKey);
  const snapshot: LocalSaleVoidSnapshot = {
    schema: "LOCAL_SALE_VOID_V1",
    localVoidId: id,
    requestKey: input.requestKey,
    organizationId: input.identity.organization_id,
    branchId: input.identity.branch_id,
    nodeId: input.identity.node_id,
    performingEmployeeId: input.performingEmployeeId,
    orderId: input.orderId,
    orderNo: input.orderNo,
    kind: input.kind,
    reason,
    sourceSurface: input.sourceSurface,
    localCommittedAt: now,
    canonicalResult: null,
  };
  const payload: LocalSaleVoidOutboxPayload = {
    schema: "LOCAL_SALE_VOID_OUTBOX_V1",
    orderId: input.orderId,
    reason,
    kind: input.kind,
    localVoidId: id,
    localCommittedAt: now,
    sourceSurface: input.sourceSurface,
  };
  const outbox: OutboxRecord = {
    requestKey: input.requestKey,
    scopeKey,
    operation: operation(input.kind),
    payload,
    state: "PENDING",
    attempts: 0,
    nextAttemptAt: null,
    lastError: null,
    createdAt: now,
    updatedAt: now,
  };

  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({
      localId: id,
      scopeKey,
      kind: "SALE_VOID",
      state: "COMMITTED",
      requestKey: input.requestKey,
      snapshot,
      createdAt: now,
      updatedAt: now,
    });
    await posRuntimeDb.outbox.put(outbox);
  });

  return snapshot;
}

async function markFailure(record: OutboxRecord, message: string) {
  const now = new Date().toISOString();
  await posRuntimeDb.outbox.put({
    ...record,
    state: "FAILED",
    attempts: record.attempts + 1,
    nextAttemptAt: new Date(Date.now() + 5_000).toISOString(),
    lastError: message,
    updatedAt: now,
  });
}

export async function syncSaleVoidOutbox(
  identity: TrustedTerminalIdentity,
  deviceSecret: string,
  sessionToken: string,
  requestKey: string,
) {
  const record = await posRuntimeDb.outbox.get(requestKey);
  if (!record || !isSaleVoidOperation(record.operation)) throw new Error("SALE_VOID_OUTBOX_NOT_FOUND");
  if (record.scopeKey !== voidScope(identity)) throw new Error("SALE_VOID_SCOPE_MISMATCH");
  if (record.state === "ACKED") {
    const journal = await posRuntimeDb.journal.where("requestKey").equals(requestKey).first();
    return journal?.snapshot as LocalSaleVoidSnapshot | undefined;
  }

  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) {
    await markFailure(record, "POS_GATEWAY_NOT_CONFIGURED");
    throw new Error("POS_GATEWAY_NOT_CONFIGURED");
  }

  const payload = record.payload as LocalSaleVoidOutboxPayload;
  const sendingAt = new Date().toISOString();
  await posRuntimeDb.outbox.put({ ...record, state: "SENDING", updatedAt: sendingAt });

  const common = {
    deviceSecret,
    sessionToken,
    orderId: payload.orderId,
    reason: payload.reason,
    requestKey,
    clientContext: {
      runtime: "WEBPOS",
      correction_type: "FULL_VOID",
      source_surface: payload.sourceSurface,
      local_void_id: payload.localVoidId,
      local_void_committed_at: payload.localCommittedAt,
      local_commit_authority: false,
    },
  };
  const result = payload.kind === "CASH"
    ? await new CashSaleVoidGatewayClient({ baseUrl }).voidSale(common)
    : await new CreditSaleVoidGatewayClient({ baseUrl }).voidSale(common);

  if (!result.ok || !result.data) {
    const message = result.error ?? (payload.kind === "CASH" ? "POS_CASH_SALE_VOID_FAILED" : "POS_CREDIT_SALE_VOID_FAILED");
    await markFailure(record, message);
    throw new Error(message);
  }

  const journal = await posRuntimeDb.journal.where("requestKey").equals(requestKey).first();
  if (!journal || journal.kind !== "SALE_VOID") {
    await markFailure(record, "LOCAL_SALE_VOID_JOURNAL_NOT_FOUND");
    throw new Error("LOCAL_SALE_VOID_JOURNAL_NOT_FOUND");
  }

  const snapshot = journal.snapshot as LocalSaleVoidSnapshot;
  const ackedAt = new Date().toISOString();
  const confirmed: LocalSaleVoidSnapshot = { ...snapshot, canonicalResult: result.data };
  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({ ...journal, state: "CONFIRMED", snapshot: confirmed, updatedAt: ackedAt });
    await posRuntimeDb.outbox.put({
      ...record,
      state: "ACKED",
      attempts: record.attempts + 1,
      nextAttemptAt: null,
      lastError: null,
      updatedAt: ackedAt,
    });
  });

  return confirmed;
}

export async function syncPendingSaleVoids(identity: TrustedTerminalIdentity, deviceSecret: string, sessionToken: string) {
  const scopeKey = voidScope(identity);
  const pending = await posRuntimeDb.outbox
    .where("operation")
    .anyOf("VOID_COMMERCE_CASH_SALE", "VOID_COMMERCE_CREDIT_SALE")
    .filter((record) => record.scopeKey === scopeKey && record.state !== "ACKED")
    .sortBy("createdAt");

  const results: Array<{ requestKey: string; ok: boolean; orderId?: string; error?: string }> = [];
  for (const record of pending) {
    if (record.nextAttemptAt && Date.parse(record.nextAttemptAt) > Date.now()) continue;
    const payload = record.payload as LocalSaleVoidOutboxPayload;
    try {
      await syncSaleVoidOutbox(identity, deviceSecret, sessionToken, record.requestKey);
      results.push({ requestKey: record.requestKey, orderId: payload.orderId, ok: true });
    } catch (error) {
      results.push({ requestKey: record.requestKey, orderId: payload.orderId, ok: false, error: error instanceof Error ? error.message : "SALE_VOID_SYNC_FAILED" });
    }
  }
  return results;
}

export async function readPendingSaleVoidOrderIds(identity: TrustedTerminalIdentity) {
  const scopeKey = voidScope(identity);
  const rows = await posRuntimeDb.journal
    .where("kind")
    .equals("SALE_VOID")
    .filter((row) => row.scopeKey === scopeKey && row.state !== "CONFIRMED")
    .toArray();
  return rows.map((row) => (row.snapshot as LocalSaleVoidSnapshot).orderId);
}
